import * as React from 'react';
import { RouteProps, Route, Redirect } from 'react-router-dom';
import client from '@api/client';
import { useLocalActor } from '@api/useLocalActor';
import { RouteNames } from '@constants/RouteNames';
import { Page } from '../../Layouts/Page/Page';

export interface IPrivateRouteProps extends RouteProps {}

/**
 * Only renders children once the API has confirmed there's a logged in actor;
 * restores the actor from a stored session token if needed, and redirects to
 * sign in if there's no valid session.
 */
export const PrivateRoute: React.FC<IPrivateRouteProps> = ({ children, render, ...rest }) => {
  const actor = useLocalActor();
  const [status, setStatus] = React.useState<'checking' | 'authenticated' | 'unauthenticated'>(
    actor ? 'authenticated' : 'checking'
  );

  React.useEffect(() => {
    if (actor) {
      setStatus('authenticated');
      return;
    }
    let cancelled = false;
    client.ensureActor().catch(() => {
      if (!cancelled) setStatus('unauthenticated');
    });
    return () => {
      cancelled = true;
    };
  }, [actor]);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (status === 'unauthenticated') {
          return <Redirect to={RouteNames.SIGN_IN} />;
        }
        if (status === 'checking') {
          return <Page isLoading />;
        }
        if (render) {
          return render(props);
        }
        return children;
      }}
    />
  );
};
